import React, { useEffect, useState } from 'react';
import Logo from '../components/Logo'
import {
    Box,
    Card,
    CardContent,
    CardHeader,
    Button,
    Typography,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Paper,
    Alert,
    Grid,
    Container,
    Divider
} from '@mui/material';
import {
    FileUpload,
    Update,
    Download,
    Add,
    Inventory
} from '@mui/icons-material';
import styled from 'styled-components';
import { Navigate, useNavigate } from 'react-router-dom';
import APIURL from '../api/apiClient';
import { message } from 'antd';
import Logout from '../components/Logout';

const UserProfileHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const UserInfoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: linear-gradient(135deg, #e0f7fa 0%, #b3e5fc 100%);
  padding: 10px 16px;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 188, 212, 0.15);
  border: 1px solid rgba(0, 188, 212, 0.2);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.3), transparent);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 25px rgba(0, 188, 212, 0.2);
    
    &::before {
      opacity: 1;
    }
  }
`;

const UserAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #00acc1, #0097a7);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 16px;
  box-shadow: 0 2px 10px rgba(0, 172, 193, 0.3);
`;


// Inline styles instead of styled components to avoid issues
const cardStyle = {
    height: '100%',
    transition: 'transform 0.3s, box-shadow 0.3s',
    '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: 8
    }
};

const actionButtonStyle = {
    marginTop: 2,
    padding: 1.5,
    width: '100%'
};

const noteBoxStyle = {
    backgroundColor: 'info.light',
    borderLeft: '4px solid',
    borderLeftColor: 'info.main',
    padding: 2,
    marginTop: 2,
    borderRadius: 1
};


const StaffOnly = ({ setSidebarVisible }) => {

    useEffect(() => {
        if (setSidebarVisible) setSidebarVisible(false);
        return () => setSidebarVisible && setSidebarVisible(true);
    }, [setSidebarVisible]);

    const [userData, setUserData] = useState({
        username: localStorage.getItem("username") || "User",
        role: localStorage.getItem("userRole") || "Employee",
    });
    const navigate = useNavigate();
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('');

    useEffect(() => {
        const handleStorageChange = () => {
            setUserData({
                username: localStorage.getItem("username") || "User",
                role: localStorage.getItem("userRole") || "Employee",
            });
        };

        window.addEventListener("storage", handleStorageChange);
        return () => window.removeEventListener("storage", handleStorageChange);
    }, []);

    const getUserInitials = (username) => {
        return username
            .split(' ')
            .map(name => name[0])
            .join('')
            .toUpperCase()
            .slice(0, 2);
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const newDataRaw = await APIURL.get("/fetchcategories.php");

                // Make sure it's an array
                const newData = Array.isArray(newDataRaw) ? newDataRaw : JSON.parse(newDataRaw);

                const formattedCategories = newData.map((category) => ({
                    value: category.id,
                    label: category.name,
                }));

                setCategories(formattedCategories);
            } catch (error) {
                console.error('Error fetching categories:', error);
            }
        };

        fetchData();
    }, []);


    const handleDownloadByCategory = () => {
        if (!selectedCategory) {
            message.warning("Please select a category first.");
            return;
        }

        const url = `${APIURL.baseURL.replace('/api', '')}/import_excel/downloaditems.php?category_id=${selectedCategory}`;
        window.open(url, "_blank");
    };

    const handleUpdateItems = () => {
        navigate('/updateitemscsv')
        // Navigation logic here
    };
    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Logo />
                <UserProfileHeader>
                    <UserInfoContainer style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <UserAvatar>
                            {getUserInitials(userData.username)}
                        </UserAvatar>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', whiteSpace: 'nowrap' }}>
                            <span style={{
                                fontSize: '14px',
                                fontWeight: 600,
                                color: '#00838f',
                                textTransform: 'capitalize',
                            }}>
                                {userData.username || 'User'}
                            </span>
                            <span style={{
                                fontSize: '12px',
                                fontWeight: 500,
                                color: '#00acc1',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                            }}>
                                {userData.role}
                            </span>
                        </div>
                    </UserInfoContainer>
                </UserProfileHeader>
            </div>

            <Container maxWidth="lg" sx={{ py: 4 }}>
                {/* Header */}
                <Paper
                    elevation={3}
                    sx={{
                        p: 2,
                        mb: 2,
                        background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
                        color: 'white',
                        textAlign: 'center',
                        borderRadius: 2
                    }}
                >
                    <Typography variant="h5" component="h1" gutterBottom>
                        <Inventory sx={{ fontSize: 30, mr: 2 }} />
                        Item Management Dashboard
                    </Typography>
                </Paper>

                <Grid container spacing={3}>
                    {/* Update Existing Items */}
                    <Grid item xs={12} md={4}>
                        <Card sx={cardStyle}>
                            <CardHeader
                                avatar={<Update color="secondary" />}
                                title={
                                    <Typography variant="h6" component="h2">
                                        Update Existing Items
                                    </Typography>
                                }
                                subheader="Modify current inventory"
                            />
                            <Divider />
                            <CardContent>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Update pricing, quantities, and details of existing items in
                                    your inventory using CSV files.
                                </Typography>

                                <Button
                                    variant="contained"
                                    color="secondary"
                                    startIcon={<FileUpload />}
                                    sx={actionButtonStyle}
                                    onClick={handleUpdateItems}
                                >
                                    Update Items via CSV
                                </Button>

                                <Box sx={{ ...noteBoxStyle, borderLeftColor: 'secondary.main' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                        Note: This functionality is for updating existing items only.
                                        Make sure your CSV includes item IDs for proper matching.
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Download CSV */}
                    <Grid item xs={12} md={4}>
                        <Card sx={cardStyle}>
                            <CardHeader
                                avatar={<Download color="success" />}
                                title={
                                    <Typography variant="h6" component="h2">
                                        Download Items Data
                                    </Typography>
                                }
                                subheader="Export inventory data"
                            />
                            <Divider />
                            <CardContent>

                                {/* Download by Category */}
                                <FormControl fullWidth sx={{ mb: 2 }}>
                                    <InputLabel id="category-select-label">Select Category *</InputLabel>
                                    <Select
                                        labelId="category-select-label"
                                        value={selectedCategory}
                                        label="Select Category *"
                                        onChange={(e) => setSelectedCategory(e.target.value)}
                                        required
                                    >
                                        {categories.map((category) => (
                                            <MenuItem key={category.value} value={category.value}>
                                                {category.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <Button
                                    variant="contained"
                                    color="success"
                                    startIcon={<Download />}
                                    sx={actionButtonStyle}
                                    onClick={handleDownloadByCategory}
                                    disabled={!selectedCategory}
                                >
                                    Download by Category
                                </Button>

                                <Alert severity="info" sx={{ mt: 2 }}>
                                    Select a category to download specific inventory data
                                </Alert>
                            </CardContent>
                        </Card>
                    </Grid>
                    {/* Additional Information */}
                    <Grid item xs={12} md={4}>
                        <Paper sx={{ p: 3, mt: 4, backgroundColor: 'background.default' }}>
                            <Typography variant="h6" gutterBottom>
                                Instructions
                            </Typography>
                            <Typography variant="body2" paragraph>
                                • Use "Update Existing Items" to modify current inventory items via CSV files
                            </Typography>
                            <Typography variant="body2" paragraph>
                                • Download inventory data filter by specific categories
                            </Typography>
                            <Typography variant="body2">
                                • Ensure all files follow the required template format for successful processing
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>

                <Logout />

            </Container>
        </div>
    )
}

export default StaffOnly
